"use client";

import { useRef } from "react";
import Image from "next/image";
import PageContent from "@/components/page-content";
import SectionHeader from "@/components/landing/section-header";
import { Globe2 } from "lucide-react";
import { Demos } from './demos';

type Demo = {
  title: string;
  image: string;
  prompt: string;
  video?: string;
};

const demos: Demo[] = [
  {
    title: "Receipt extraction",
    image: "/images/walmart_receipt.png",
    prompt: "\"Extract the total, date, and store name\"",
  },
  {
    title: "Security monitoring",
    image: "/images/robbery_example.jpeg",
    prompt: "\"What is happening in this scene?\"",
  },
  {
    title: "Medical imaging",
    image: "/images/veins.png",
    prompt: "\"Describe any abnormalities visible\"",
  },
] satisfies Demo[];

function DemoCard({ demo }: { demo: Demo }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const handleEnter = () => {
    if (demo.video && videoRef.current) {
      videoRef.current.play().catch(() => { });
    }
  };

  const handleLeave = () => {
    if (demo.video && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className="group rounded-2xl border border-border/70 bg-card/60 shadow-sm transition hover:-translate-y-1 hover:shadow-md"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <div className="relative overflow-hidden rounded-t-2xl aspect-[4/3]">
        {demo.video && (
          <video
            ref={videoRef}
            muted
            playsInline
            className="absolute inset-0 h-full w-full object-cover object-center opacity-0 transition duration-300 group-hover:opacity-100"
            poster={demo.image}
          >
            <source src={demo.video} type="video/mp4" />
          </video>
        )}
        <Image
          src={demo.image}
          alt={demo.title}
          width={640}
          height={400}
          className={
            demo.video
              ? "h-full w-full object-cover object-center transition duration-300 group-hover:opacity-0"
              : "h-full w-full object-cover object-center transition duration-300 group-hover:scale-105"
          }
        />
      </div>
      <div className="flex flex-col gap-1.5 px-5 py-4">
        <h3 className="text-lg font-semibold">{demo.title}</h3>
        <p className="text-sm text-muted-foreground">
          Query: <span className="font-medium text-foreground">{demo.prompt}</span>
        </p>
      </div>
    </div>
  );
}

export function RealWorldDemos() {
  return (
    <PageContent>
      <SectionHeader
        sectionIcon={<Globe2 className="size-4" />}
        section="Real-world demos"
        heading="Visual Understanding, Instantly"
        description="Same API, endless applications. See Query across different domains."
        className="pb-4"
      />
      <div className="flex flex-col gap-4">
        <Demos />
        {/* Bottom row of demos hidden but kept in code */}
        {false && (
          <div className="pt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {demos.map((demo) => (
              <DemoCard key={demo.title} demo={demo} />
            ))}
          </div>
        )}
      </div>
    </PageContent >
  );
}


